import { useEffect, useState } from "react";
import {
  Link,
  Outlet,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { AppEvent, RunDetail } from "@ideacrunch/shared";
import { subscribeAppEvents, useRun, useRunEvents } from "../api";
import { AttachFiles, uploadBatch } from "../components/AttachFiles";
import { Pipeline } from "../components/Pipeline";
import {
  BriefingDownload,
  LongText,
  MarkdownView,
  relativeTime,
  StatTile,
  statusLabel,
} from "../components/bits";
import { useVocab } from "../vocab";

export function useRunContext(): RunDetail {
  return useOutletContext<RunDetail>();
}

export function RunView() {
  const { runId = "" } = useParams();
  const { data: run, isLoading, error } = useRun(runId);

  if (isLoading) return <div className="loading">Loading run...</div>;
  if (error || !run) {
    return <div className="error-box">Run not found: {runId}</div>;
  }

  // An idle session lingers after the Archivist's last turn; on a finished
  // run "engine waiting" reads as a hang (asked 2026-10-27), so only a run
  // that can still move shows the idle engine's state.
  const finished = run.status === "complete" || run.status === "killed";

  return (
    <div>
      <div className="breadcrumbs">
        <Link to="/">Ideas</Link> / {run.id}
      </div>
      <div className="run-header">
        <h1>{run.title}</h1>
        <div className="sub">
          <span
            className={`chip ${run.pendingGate !== null ? "warn" : run.status === "killed" ? "critical" : run.status === "complete" ? "good" : "ghost"}`}
          >
            {statusLabel(run)}
          </span>
          {run.engine === "running" && (
            <span className="chip accent pulsing">engine working</span>
          )}
          {run.engine === "awaiting-input" &&
            run.pendingGate === null &&
            !finished && <span className="chip ghost">engine waiting</span>}
          {run.engine === "parked" && !finished && (
            <span className="chip ghost">engine parked - a message wakes it</span>
          )}
          {run.depth !== "unknown" && (
            <span className="chip ghost">{run.depth}</span>
          )}
          {run.mode !== "unknown" && (
            <span className="chip ghost">
              {run.mode === "A"
                ? "problem-first"
                : run.mode === "B"
                  ? "solution under test"
                  : "scouted"}
            </span>
          )}
          {run.basisRun && (
            <Link
              className="chip ghost"
              to={`/runs/${encodeURIComponent(run.basisRun)}`}
              title={`This run builds on ${run.basisRun}`}
            >
              builds on {run.basisRun.replace(/^\d{4}-\d{2}-\d{2}-/, "")}
            </Link>
          )}
          <span>{relativeTime(run.lastActivity)}</span>
        </div>
      </div>
      <Pipeline run={run} />
      <EngineWaitingPanel run={run} />
      <LiveActivity run={run} />
      <MessageTheMachine run={run} />
      <Outlet context={run} />
      <DeleteIdea run={run} />
    </div>
  );
}

/**
 * Delete an idea at any point in the process. Distinct from a gate kill
 * (which records a verdict and deposits residue): this is workspace hygiene
 * for false starts and restarts. The server stops engine work, removes the
 * run folder, and commits the removal - recoverable from git history only.
 */
function DeleteIdea({ run }: { run: RunDetail }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [state, setState] = useState<"idle" | "deleting" | "error">("idle");
  const [message, setMessage] = useState("");

  const doDelete = async () => {
    setState("deleting");
    try {
      const res = await fetch(`/api/runs/${encodeURIComponent(run.id)}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `${res.status}`);
      }
      await queryClient.invalidateQueries({ queryKey: ["runs"] });
      navigate("/");
    } catch (err) {
      setState("error");
      setMessage(err instanceof Error ? err.message : String(err));
    }
  };

  if (!confirming) {
    return (
      <div
        style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}
      >
        <button
          className="chip ghost clickable"
          onClick={() => setConfirming(true)}
        >
          delete this idea
        </button>
      </div>
    );
  }

  return (
    <div
      className="panel"
      style={{
        borderColor: "var(--critical)",
        background: "var(--wash-critical)",
        marginTop: 24,
      }}
    >
      <h2 style={{ color: "var(--critical)" }}>Delete this idea?</h2>
      <p style={{ color: "var(--ink-2)", fontSize: 13.5 }}>
        This removes the run folder and its index from your workspace and
        stops any engine work on it. The removal is recorded as a git commit,
        so history stays recoverable by hand - but the app treats it as gone.
        If the idea deserves a verdict, kill it at a gate instead: kills feed
        the library.
      </p>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          className="btn"
          style={{ background: "var(--critical)" }}
          disabled={state === "deleting"}
          onClick={() => void doDelete()}
        >
          {state === "deleting" ? "Deleting..." : "Delete for good"}
        </button>
        <button
          className="btn secondary"
          disabled={state === "deleting"}
          onClick={() => setConfirming(false)}
        >
          Keep it
        </button>
      </div>
      {state === "error" && (
        <p style={{ color: "var(--critical)" }}>{message}</p>
      )}
    </div>
  );
}

const STOP_LINE = "the machine finished its turn and is waiting";

/** Generic stops (no explicit ask block) wait this long before prompting:
 *  a gate arrival flips pendingGate within seconds of the stop, and the
 *  panel must not flash while the parser catches up. */
const STOP_GRACE_MS = 30_000;

/**
 * The machine stopped OUTSIDE a gate. The panel renders the durable
 * "engine.stopped" event frozen at turn end - the explicit QUESTIONS FOR
 * YOU block as an answerable list, or the final prose for a generic stop -
 * never the rotating live activity feed. A new stop can only exist after
 * the previous one was answered (the engine cannot continue without input),
 * so questions no longer vanish mid-composition.
 */
function EngineWaitingPanel({ run }: { run: RunDetail }) {
  const events = useRunEvents(run.id);
  const [reply, setReply] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">(
    "idle",
  );
  const [message, setMessage] = useState("");
  const [now, setNow] = useState(() => Date.now());

  const all = events.data ?? [];
  // Latest durable stop; runs stopped before the protocol existed fall back
  // to the legacy stop line (no questions, no frozen text).
  const stops = all.filter((e) => e.type === "engine.stopped");
  const legacy = all.filter(
    (e) => e.type === "engine.activity" && e.text.startsWith(STOP_LINE),
  );
  const stop =
    stops[stops.length - 1] ??
    (legacy.length > 0
      ? { ...legacy[legacy.length - 1]!, text: "", questions: [] }
      : null);
  const lastAnswer = all.reduce(
    (max, e) =>
      (e.type === "user-reply" || e.type === "gate-decision-submitted") &&
      e.ts > max
        ? e.ts
        : max,
    0,
  );

  const questions = stop?.questions ?? [];
  const stopTs = stop?.ts ?? 0;
  const generic = stop !== null && questions.length === 0;

  // Re-render once a generic stop's grace period elapses.
  useEffect(() => {
    if (!generic) return;
    const remaining = stopTs + STOP_GRACE_MS - Date.now();
    if (remaining <= 0) return;
    const t = setTimeout(() => setNow(Date.now()), remaining + 250);
    return () => clearTimeout(t);
  }, [generic, stopTs]);

  const waitingOutsideGate =
    run.pendingGate === null &&
    run.status === "in-progress" &&
    (run.engine === "awaiting-input" || run.engine === "parked");
  const inGrace = generic && now < stopTs + STOP_GRACE_MS;

  if (!waitingOutsideGate || stop === null || stopTs <= lastAnswer || inGrace) {
    return null;
  }

  const answeredCount = questions.filter((_, i) => answers[i]?.trim()).length;
  const composed =
    questions.length > 0
      ? questions
          .map((_, i) => answers[i]?.trim())
          .map((a, i) => (a ? `(${i + 1}) ${a}` : null))
          .filter((x): x is string => x !== null)
          .join(" ")
      : reply.trim();

  const send = async (text: string, kind: "reply" | "resume") => {
    setState("sending");
    try {
      const uploadBatchId =
        kind === "reply" && files.length > 0 ? await uploadBatch(files) : null;
      const res = await fetch(
        `/api/runs/${encodeURIComponent(run.id)}/${kind === "reply" ? "reply" : "resume"}`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: kind === "reply" ? JSON.stringify({ text, uploadBatchId }) : undefined,
        },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `${res.status}`);
      }
      setState("sent");
      setMessage(
        "Delivered. The machine resumes now (or as soon as an engine slot frees) - watch the activity feed.",
      );
    } catch (err) {
      setState("error");
      setMessage(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="gate-banner" style={{ marginBottom: 16 }}>
      <h1>The machine stopped and needs a word from you</h1>
      <div className="verdict-line" style={{ marginBottom: 8 }}>
        {questions.length > 0
          ? "It worked through everything it could and batched what remains - answer below and it continues."
          : "This is not a gate - the engine paused mid-phase without explicit questions. Give it direction, or just continue."}
      </div>
      {stop.text && (
        <div className="skeptic-note" style={{ marginBottom: 8 }}>
          <MarkdownView text={stop.text} collapsible />
        </div>
      )}
      {state !== "sent" ? (
        <div className="gate-form">
          {questions.length > 0 ? (
            questions.map((q, i) => (
              <div key={i} style={{ marginBottom: 12 }}>
                <div style={{ marginBottom: 6 }}>
                  <span className="chip accent">Q{i + 1}</span> <span>{q}</span>
                </div>
                <textarea
                  rows={2}
                  placeholder="Your answer"
                  value={answers[i] ?? ""}
                  onChange={(e) =>
                    setAnswers((prev) => ({ ...prev, [i]: e.target.value }))
                  }
                />
              </div>
            ))
          ) : (
            <textarea
              rows={3}
              placeholder="Your answer / instructions for the machine (recorded in the activity log)"
              value={reply}
              onChange={(e) => setReply(e.target.value)}
            />
          )}
          <div style={{ marginTop: 8 }}>
            <AttachFiles files={files} onChange={setFiles} />
          </div>
          <div
            style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}
          >
            <button
              className="btn"
              disabled={
                state === "sending" || (composed === "" && files.length === 0)
              }
              onClick={() => void send(composed, "reply")}
            >
              {state === "sending"
                ? "Sending..."
                : questions.length > 0
                  ? `Send answers (${answeredCount}/${questions.length})`
                  : "Send reply"}
            </button>
            <button
              className="btn secondary"
              disabled={state === "sending"}
              onClick={() => void send("", "resume")}
            >
              Just continue from the files
            </button>
          </div>
          {state === "error" && (
            <p style={{ color: "var(--critical)" }}>{message}</p>
          )}
        </div>
      ) : (
        <p style={{ color: "var(--good-text)" }}>{message}</p>
      )}
    </div>
  );
}

/**
 * Always-available channel to the engine, independent of stops and gates.
 * Born from a live incident: a run looked dead (finished work, no visible
 * gate, engine parked) and the only way to ask "what is going on?" was a
 * developer opening Claude Code on the server. Delivery uses the existing
 * /reply path, which already handles every engine state: running (queued,
 * read at the start of the next turn), awaiting-input or parked (wakes the
 * session), and no session at all (fresh continue from the files).
 */
function MessageTheMachine({ run }: { run: RunDetail }) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">(
    "idle",
  );
  const [message, setMessage] = useState("");

  if (
    run.engine === "offline" ||
    run.status === "complete" ||
    run.status === "killed"
  ) {
    return null;
  }

  const hint =
    run.engine === "running"
      ? "The machine is working. Your message is delivered when its current step finishes - ask a question, add context, or redirect it."
      : "The machine is not working right now. Your message wakes it - ask a question, add context, or tell it what to do next.";

  const send = async () => {
    setState("sending");
    try {
      const uploadBatchId =
        files.length > 0 ? await uploadBatch(files) : null;
      const res = await fetch(
        `/api/runs/${encodeURIComponent(run.id)}/reply`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ text: text.trim(), uploadBatchId }),
        },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `${res.status}`);
      }
      setState("sent");
      setText("");
      setFiles([]);
      setMessage(
        "Delivered. The answer lands in the activity feed and in the machine's next stop.",
      );
    } catch (err) {
      setState("error");
      setMessage(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <details className="section-acc" style={{ marginBottom: 16 }}>
      <summary>Message the machine - questions, context, direction</summary>
      <div className="gate-form" style={{ marginTop: 8 }}>
        <p style={{ color: "var(--ink-2)", fontSize: 13.5, marginTop: 0 }}>
          {hint}
        </p>
        <textarea
          rows={3}
          placeholder="e.g. Where does this run stand? / Also weigh the Canadian market. / Stop and wait for me."
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            if (state === "sent" || state === "error") setState("idle");
          }}
        />
        <div style={{ marginTop: 8 }}>
          <AttachFiles files={files} onChange={setFiles} />
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
          <button
            className="btn"
            disabled={
              state === "sending" || (text.trim() === "" && files.length === 0)
            }
            onClick={() => void send()}
          >
            {state === "sending" ? "Sending..." : "Send to the machine"}
          </button>
          {state === "sent" && (
            <span style={{ color: "var(--good-text)", fontSize: 13.5 }}>
              {message}
            </span>
          )}
          {state === "error" && (
            <span style={{ color: "var(--critical)", fontSize: 13.5 }}>
              {message}
            </span>
          )}
        </div>
      </div>
    </details>
  );
}

interface FeedLine {
  ts: number;
  text: string;
  lane: string | null;
}

/**
 * What the machine is doing right now: a prominent ticker with the latest
 * action plus an expandable feed. Backfills from the persisted event log,
 * then stays live off the SSE stream. Hints only - the pipeline is the fact.
 */
function LiveActivity({ run }: { run: RunDetail }) {
  const backfill = useRunEvents(run.id);
  const [liveLines, setLiveLines] = useState<FeedLine[]>([]);

  useEffect(() => {
    return subscribeAppEvents((event: AppEvent) => {
      if (event.type === "engine.activity" && event.runId === run.id) {
        setLiveLines((prev) =>
          [
            { ts: Date.parse(event.at), text: event.text, lane: event.lane },
            ...prev,
          ].slice(0, 150),
        );
      } else if (event.type === "sync.conflict") {
        setLiveLines((prev) =>
          [
            { ts: Date.now(), text: `SYNC: ${event.detail}`, lane: null },
            ...prev,
          ].slice(0, 150),
        );
      }
    });
  }, [run.id]);

  // Merge persisted history with live lines, newest first, deduped.
  const merged: FeedLine[] = [...liveLines];
  for (const e of [...(backfill.data ?? [])].reverse()) {
    if (!merged.some((l) => l.ts === e.ts && l.text === e.text)) {
      merged.push({ ts: e.ts, text: e.text, lane: e.lane });
    }
  }
  merged.sort((a, b) => b.ts - a.ts);

  const engineActive = run.engine === "running";
  if (merged.length === 0 && !engineActive) return null;

  const latest = merged[0];

  return (
    <div className="activity-box">
      <div className="activity-now">
        <span className={`dot ${engineActive ? "live" : ""}`} />
        {engineActive ? (
          <span>
            <strong>Working:</strong>{" "}
            {latest ? formatLine(latest) : "the engine is thinking..."}
          </span>
        ) : latest ? (
          <span>
            <strong>Last activity:</strong> {formatLine(latest)}{" "}
            <span style={{ color: "var(--muted)" }}>
              ({relativeTime(new Date(latest.ts).toISOString())})
            </span>
          </span>
        ) : null}
      </div>
      {merged.length > 1 && (
        <details className="section-acc">
          <summary>Everything the machine has done ({merged.length})</summary>
          <div className="activity-feed">
            {merged.map((l, i) => (
              <div key={`${l.ts}-${i}`} className="activity-line">
                <span className="activity-time">
                  {new Date(l.ts).toTimeString().slice(0, 8)}
                </span>
                {l.lane && <span className="chip accent">{l.lane}</span>}
                <span>
                  <LongText text={l.text} max={200} />
                </span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function formatLine(line: FeedLine): string {
  return line.lane ? `[${line.lane}] ${line.text}` : line.text;
}

export function RunOverview() {
  const run = useRunContext();
  const { skepticReviewTitle } = useVocab();

  const totalClaims = run.lanes.reduce((n, l) => n + l.claimCount, 0);
  const decidedGates = run.gates.filter((g) => g.decisionRaw).length;
  const verdictToken = run.verdict?.split(/[\s(]/)[0];
  // Completed runs route to the take-with-you page when the run has one;
  // runs archived before 07-next-steps.md existed keep the plain summary.
  const nextSteps = run.artifacts.find((a) => a.kind === "next-steps");

  return (
    <>
    <div className="panel">
      <h2>Where this idea stands</h2>
      <p className="status-sentence">
        {run.pendingGate !== null
          ? `The machine is stopped and waiting for you: ${statusLabel(run).toLowerCase()}.`
          : run.status === "complete"
            ? `Run complete. Final verdict: ${verdictToken ?? "recorded in the recommendation"}.`
            : run.status === "killed"
              ? "This idea was killed - the residue and library deposits survive."
              : "The machine is working. It will stop when it needs your call."}
      </p>
      {nextSteps && (run.status === "complete" || run.status === "killed") && (
        <p style={{ margin: "0 0 12px" }}>
          <Link className="btn" to={`/runs/${run.id}/a/${nextSteps.relPath}`}>
            Open your next steps
          </Link>
        </p>
      )}
      <div className="stat-row">
        <StatTile value={run.phase} label="of 7 phases" />
        <StatTile value={totalClaims} label="claims gathered" />
        <StatTile value={decidedGates} label="of 3 gates decided" />
        {verdictToken && (
          <StatTile
            value={verdictToken}
            label="verdict"
            tone={
              verdictToken === "PURSUE"
                ? "good"
                : verdictToken === "KILL"
                  ? "critical"
                  : "warn"
            }
          />
        )}
      </div>
      <p style={{ color: "var(--ink-2)", fontSize: "13.5px" }}>
        Click any node in the pipeline to read its artifact. The{" "}
        {skepticReviewTitle} is the independent quality check: it reads only the
        files, never the conversation that produced them.
      </p>
    </div>
    <BriefingDownload run={run} />
    </>
  );
}
